import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  Skeleton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  apiGetOutsourcedJobApplications,
  apiGetOutsourcedRequestById,
} from "../../api/outsourcing";
import { formatDate } from "../../utils/url-id";
import { ArrowBack } from "@mui/icons-material";

const OutsourcedApplications = () => {
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const [page, setPage] = useState(1);

  // Fetch outsourcing request details
  const { data: requestData, isLoading: requestLoading } = useQuery({
    queryKey: ["outsourced-request", requestId],
    queryFn: () =>
      apiGetOutsourcedRequestById({
        requestId: requestId!,
      }),
    enabled: !!requestId,
  });

  // Fetch applications
  const { data: applicationsData, isLoading: applicationsLoading } = useQuery({
    queryKey: ["outsourced-applications", requestId, page],
    queryFn: () =>
      apiGetOutsourcedJobApplications({
        requestId: requestId!,
        page: page.toString(),
        filter: "",
      }),
    enabled: !!requestId,
  });

  const request = requestData?.data?.data;
  const applications = applicationsData?.data?.data || [];

  const getStatusColor = (status: string) => {
    const colors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      pending: "warning",
      shortlisted: "info",
      interviewing: "primary",
      accepted: "success",
      rejected: "error",
    };
    return colors[status] || "default";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/outsourcing")}
        sx={{ mb: 2 }}
      >
        Back to Outsourced Jobs
      </Button>

      {requestLoading ? (
        <Skeleton
          variant="rectangular"
          height={200}
          sx={{ mb: 3, borderRadius: 2 }}
        />
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                  {request?.job_info.job_title}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Chip label={request?.job_info.job_type} />
                  <Chip label={request?.job_info.department} />
                  <Chip
                    label={request?.outsourcing_details.urgency.toUpperCase()}
                    color={
                      request?.outsourcing_details.urgency === "critical"
                        ? "error"
                        : request?.outsourcing_details.urgency === "high"
                          ? "warning"
                          : "info"
                    }
                  />
                </Stack>
                <Typography variant="body2" color="textSecondary">
                  <strong>Facility:</strong> {request?.facility_info.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Location:</strong> {request?.facility_info.city},{" "}
                  {request?.facility_info.state}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Positions: {request?.stats.positions_filled}/
                    {request?.outsourcing_details.positions_needed}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      label={`${request?.stats.total_applications_reviewed || 0} Applications`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${request?.stats.candidates_shortlisted || 0} Shortlisted`}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`${request?.stats.candidates_hired || 0} Hired`}
                      size="small"
                      color="success"
                    />
                  </Stack>
                  <Typography variant="caption" color="textSecondary">
                    Requested:{" "}
                    {formatDate(request?.outsourcing_details.requested_date)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Applications ({applications.length})
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Medic Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Interview</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Applied</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applicationsLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 7 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No applications found for this outsourced job
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              applications.map((application: any) => (
                <TableRow key={application._id} hover>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {application.medic_info.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {application.medic_info.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{application.medic_info.medic_type}</TableCell>
                  <TableCell>{application.medic_info.state}</TableCell>
                  <TableCell>
                    <Chip
                      label={application.status.toUpperCase()}
                      color={getStatusColor(application.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {application.interview?.interview_date ? (
                      <Typography variant="caption">
                        {formatDate(application.interview.interview_date)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Not scheduled
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(application.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/outsourcing/${requestId}/applications/${application._id}`,
                        )
                      }
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OutsourcedApplications;
